import React, { useState, createElement } from 'react';
import { DownloadIcon, FileSpreadsheetIcon, CheckCircleIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { getCsvTemplate } from './utils';
import { UploadDropzone } from '../../../components/ui/upload-dropzone';
interface CsvUploadFormProps {
  onImportComplete: () => void;
}
export const CsvUploadForm: React.FC<CsvUploadFormProps> = ({
  onImportComplete
}) => {
  const dk = useDarkClasses();
  const [isParsing, setIsParsing] = useState(false);
  const [parseResult, setParseResult] = useState<{
    total: number;
    valid: number;
    errors: number;
  } | null>(null);
  const handleDownloadTemplate = () => {
    const csvContent = getCsvTemplate();
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'aftertide_book_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleUploadComplete = (files: any) => {
    setIsParsing(true);
    // Simulate parsing
    setTimeout(() => {
      setParseResult({
        total: 42,
        valid: 38,
        errors: 4
      });
      setIsParsing(false);
    }, 2000);
  };
  const handleAddToQueue = () => {
    onImportComplete();
    setParseResult(null);
  };
  return <div className="space-y-8" data-id="element-18320" data-mp="38mlo7zhbl49s">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-18321" data-mp="33n90zdmjdv6o">
        <div data-id="element-18322" data-mp="33lei04utsd58">
          <h3 className={`font-bold mb-1 ${dk.textHeading}`} data-id="element-18323" data-mp="3ibvj1n3jt11l">
            Bulk Upload via CSV
          </h3>
          <p className={`text-sm ${dk.textSecondary}`} data-id="element-18324" data-mp="3dkx0shz01svp">
            Upload a spreadsheet containing your catalog. Download the template
            to see the expected format.
          </p>
        </div>
        <button onClick={handleDownloadTemplate} className={`flex items-center shrink-0 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${dk.border} ${dk.textPrimary} hover:bg-gray-50 dark:hover:bg-gray-800`} data-id="element-18325" data-mp="2kx9khf4rhoev">
          <DownloadIcon className="w-4 h-4 mr-2" data-id="element-18326" data-mp="tqlfmp97deft" />
          Download Template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18327" data-mp="ylsftn39k0zz">
        <div data-id="element-18328" data-mp="12jq6yvglgx60">
          <h4 className={`text-sm font-bold mb-3 ${dk.textPrimary}`} data-id="element-18329" data-mp="12k87i4aahxjq">
            Required Columns
          </h4>
          <ul className={`text-sm space-y-1 ${dk.textSecondary} list-disc pl-5`} data-id="element-18330" data-mp="5wtru9cgm4mr">
            <li data-id="element-18331" data-mp="avol6581zbl4">
              <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded" data-id="element-18332" data-mp="evoytnnbvir4">
                title
              </code>
            </li>
            <li data-id="element-18333" data-mp="jw38tb8hp2rs">
              <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded" data-id="element-18334" data-mp="jsfh8hmluqfp">
                author_name
              </code>
            </li>
            <li data-id="element-18335" data-mp="1v8nm4ppieduj">
              <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded" data-id="element-18336" data-mp="1v8dy37o0ygrh">
                format
              </code>{' '}
              (ebook, paperback, hardcover, audiobook)
            </li>
          </ul>
        </div>
        <div data-id="element-18337" data-mp="20gvglny775iq">
          <h4 className={`text-sm font-bold mb-3 ${dk.textPrimary}`} data-id="element-18338" data-mp="1vnkgs6rszp0b">
            Optional Enrichment Columns
          </h4>
          <p className={`text-xs mb-2 ${dk.textMuted}`} data-id="element-18339" data-mp="1rn0hawjrc9b0">
            Include these to skip manual entry later:
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-18340" data-mp="1mop90q9426uw">
            {['isbn_13', 'asin', 'series_name', 'publication_date', 'blurb', 'content_warnings', 'spice_level', 'amazon_url', 'bookshop_url'].map(col => <code key={col} className={`text-xs px-1.5 py-0.5 rounded ${dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-18341" data-mp="372pkne3o7tli">
                {col}
              </code>)}
            <span className={`text-xs ${dk.textMuted}`} data-id="element-18342" data-mp="3a53xpkp2wp2v">+ 10 more</span>
          </div>
        </div>
      </div>

      {!parseResult ? <div className={isParsing ? 'opacity-50 pointer-events-none' : ''} data-id="element-18343" data-mp="313w5nkd5okh8">
          <UploadDropzone onUploadComplete={handleUploadComplete} images={[]} maxFiles={1} data-id="element-18344" data-mp="315yvo4eb6dly" />
          {isParsing && <p className="text-center mt-4 text-sm font-medium text-purple-600 animate-pulse" data-id="element-18345" data-mp="2rdb2djmjerrz">
              Parsing CSV...
            </p>}
        </div> : <div className={`p-6 rounded-xl border ${dk.card} ${dk.border}`} data-id="element-18346" data-mp="2hgogqsmdzwow">
          <div className="flex items-center justify-center mb-6" data-id="element-18347" data-mp="2mezsxa05mjaa">
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full" data-id="element-18348" data-mp="2mf9hct5jwidv">
              <FileSpreadsheetIcon className="w-8 h-8 text-green-600 dark:text-green-400" data-id="element-18349" data-mp="28gjojpprl4vb" />
            </div>
          </div>

          <h3 className={`text-center font-bold text-lg mb-2 ${dk.textHeading}`} data-id="element-18350" data-mp="2dgearfylh6a1">
            Parse Complete
          </h3>

          <div className="flex justify-center gap-6 mb-8 text-center" data-id="element-18351" data-mp="1ou8b1c7v17f2">
            <div data-id="element-18352" data-mp="22t802kzabobr">
              <p className={`text-2xl font-bold ${dk.textPrimary}`} data-id="element-18353" data-mp="22tpudc2dzsh1">
                {parseResult.total}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-18354" data-mp="1yqtgfynskkjf">
                Total Rows
              </p>
            </div>
            <div data-id="element-18355" data-mp="2cp19vwlmd3pu">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400" data-id="element-18356" data-mp="27skjevqy6d9l">
                {parseResult.valid}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-18357" data-mp="2mfvwj22s25bt">
                Valid
              </p>
            </div>
            <div data-id="element-18358" data-mp="2hkgkmfobyji1">
              <p className={`text-2xl font-bold ${parseResult.errors > 0 ? 'text-red-500' : dk.textPrimary}`} data-id="element-18359" data-mp="2vhlxozmgoims">
                {parseResult.errors}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-18360" data-mp="1o5mkgbvcjqld">
                Errors
              </p>
            </div>
          </div>

          <div className="flex justify-center" data-id="element-18361" data-mp="1t4z3smn0efj7">
            <button onClick={handleAddToQueue} className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-18362" data-mp="1x6ayh6kqpqy9">
              Add {parseResult.valid} to Queue
            </button>
          </div>
        </div>}
    </div>;
};